import { CasinoGame } from 'types/CasinoGame';
import styles from './CasinoGameListItem.module.css';
import classNames from 'classnames';
import { useContext } from 'react';
import { UserContext } from 'context/userContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getGameUrl, getLiveCasinoGameUrl } from 'services/api/gameApiService';
import { ToastMessagesIDs } from 'types/enums';
import { usePlayerLayout } from 'lib/playerLayout';
import { casinoProviderLabel } from 'helpers/casinoProvider';

interface Props {
  game: CasinoGame;
  startGameLoad: () => number;
  completeGameLoad: (seq: number, url: string) => void;
  failGameLoad: (seq: number) => void;
  useLiveCasino?: boolean;
  /** Load the picture only near the screen: for the classic lists, which
   *  render every provider's games, collapsed ones too. Not for the modern
   *  rails, where lazy pictures in a sideways scroller stayed blank. */
  lazy?: boolean;
  /** Starred by the player; the star toggles it (see useCasinoFavorites). */
  favorite?: boolean;
  onToggleFavorite?: () => void;
}

export const CasinoGameListItem = ({
  game,
  startGameLoad,
  completeGameLoad,
  failGameLoad,
  useLiveCasino = false,
  lazy = false,
  favorite = false,
  onToggleFavorite,
}: Props) => {
  const { t } = useTranslation();
  const { loggedUser } = useContext(UserContext);
  const vip = usePlayerLayout() === 'vip';

  const handleClickOnGame = async (game: CasinoGame) => {
    if (loggedUser == null) {
      toast(t('must_be_logged_in'), {
        toastId: ToastMessagesIDs.CASINO_GAMES_ACCESS_ERROR,
      });
    } else {
      const seq = startGameLoad();
      try {
        const getUrl = useLiveCasino ? getLiveCasinoGameUrl : getGameUrl;
        const nextUrl = await getUrl(
          game.uuid,
          loggedUser.id,
          loggedUser.username
        );
        completeGameLoad(seq, nextUrl);
      } catch {
        failGameLoad(seq);
      }
    }
  };

  return (
    <div className={styles.gameWrapper} onClick={() => handleClickOnGame(game)}>
      <img
        src={game.image}
        alt={`${game.name}`}
        loading={lazy ? 'lazy' : undefined}
        decoding="async"
      />
      {useLiveCasino && vip && (
        <span className={styles.vipLive}>
          <i />
          {t('footer_live_label')}
        </span>
      )}
      <p>
        {game.name}
        {vip && game.provider && (
          <small>{casinoProviderLabel(game.provider)}</small>
        )}
      </p>
      {onToggleFavorite && (
        <button
          type="button"
          className={classNames(styles.favorite, favorite && styles.favoriteOn)}
          aria-pressed={favorite}
          aria-label={t(
            favorite ? 'casino_remove_favorite' : 'casino_add_favorite'
          )}
          title={t(favorite ? 'casino_remove_favorite' : 'casino_add_favorite')}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite();
          }}
        >
          {favorite ? '★' : '☆'}
        </button>
      )}
      <div className={styles.playButton} />
    </div>
  );
};
